/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Viewport-aware fixed-position dropdown placement for action and filter menus.
 * @module utils/ui/dropdownPosition
 */

/**
 * @param {DOMRect} buttonRect
 * @param {Object} [options]
 * @param {number} [options.dropdownHeight=80]
 * @param {number} [options.dropdownWidth=140]
 * @param {number} [options.padding=4]
 * @returns {{ top: number, left: number, shouldOpenAbove: boolean }}
 */
export const calculateDropdownPosition = (
  buttonRect,
  {
    dropdownHeight = 80,
    dropdownWidth = 140,
    padding = 4,
  } = {},
) => {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  const spaceBelow = viewportHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;

  const shouldOpenAbove =
    spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  const top = shouldOpenAbove
    ? buttonRect.top + window.scrollY - dropdownHeight - padding
    : buttonRect.bottom + window.scrollY + padding;

  let left = buttonRect.right - dropdownWidth;
  if (left < 0) {
    left = buttonRect.left;
  }
  if (left + dropdownWidth > viewportWidth) {
    left = viewportWidth - dropdownWidth - 10;
  }

  return {
    top,
    left,
    shouldOpenAbove,
  };
};

/**
 * Filters dropdown variant with a left offset for alignment with the settings button.
 * @param {DOMRect} buttonRect
 * @returns {{ top: number, left: number, shouldOpenAbove: boolean }}
 */
export const calculateFiltersDropdownPosition = (buttonRect) => {
  const dropdownHeight = 200;
  const dropdownWidth = 200;
  const padding = 4;
  const leftOffset = 33;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  const spaceBelow = viewportHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;

  const shouldOpenAbove =
    spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  const top = shouldOpenAbove
    ? buttonRect.top + window.scrollY - dropdownHeight - padding
    : buttonRect.bottom + window.scrollY + padding;

  let left = buttonRect.right - dropdownWidth - leftOffset;
  if (left < 0) {
    left = buttonRect.left - dropdownWidth + leftOffset;
  }
  if (left + dropdownWidth > viewportWidth) {
    left = viewportWidth - dropdownWidth - 10;
  }

  return {
    top,
    left,
    shouldOpenAbove,
  };
};
